import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/api";
import { Modal } from "../albums/Modal";
import { PlatformIcon, useLinkPlatforms } from "../profile/links";
import { rooms, type Room } from "./api";
import { OverlayDialog } from "./OverlayDialog";

/**
 * How the room behaves, in one place you open on purpose.
 *
 * ## Why these left the deck column
 *
 * They used to sit under the transport, in the column that also held the
 * artwork and the play button - a column doing three unrelated jobs: showing
 * what is playing, controlling it, and configuring the room. The third is the
 * odd one out. A host decides whether they accept explicit tracks once, and
 * then never touches it again for the rest of the set.
 *
 * Permanent screen space for a decision made once is space taken from the
 * queue, which is the thing a host actually watches. So they moved here, and
 * the column they were in went to the studio.
 *
 * ## Everything here saves on change
 *
 * There is no Save button and there should not be. Each control is a single
 * field with its own request, the room is live while they are being changed,
 * and a dialog that batched them would leave a host wondering whether their
 * change had taken effect while people were listening.
 */
export function RoomSettingsDialog({
  room,
  onChange,
  onClose,
}: {
  room: Room;
  onChange: () => void;
  onClose: () => void;
}) {
  return (
    <Modal title="Room settings" onClose={onClose} wide>
      <div className="flex flex-col gap-8" data-room-settings>
        <SubmissionsSetting room={room} onChange={onChange} />
        <ExplicitSetting room={room} onChange={onChange} />
        <AutoPlaySetting room={room} onChange={onChange} />
        <ShowQueueSetting room={room} onChange={onChange} />
        <SimulcastSetting room={room} onChange={onChange} />

        {/* The viewer this token was generated for now exists. */}
        {room.overlay_token && <OverlayLink token={room.overlay_token} />}
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Where else this set is going out.
 *
 * ## Only somewhere they can actually be watched
 *
 * Two filters, and neither is arbitrary. The platform has to carry live video,
 * because "watch live on Spotify" is a button that goes somewhere with nothing
 * live on it. And they have to have the link on their profile, because that
 * link *is* the badge's address - there is no second field to paste a stream
 * URL into, on purpose, so an artist who changes their Twitch handle changes it
 * once.
 *
 * The consequence is that a host with no eligible links is offered nothing and
 * told why, rather than a dropdown that rejects every choice in it.
 */
function SimulcastSetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const { platforms } = useLinkPlatforms();

  /*
   * Their own links, from their public profile.
   *
   * `/me` is the obvious-looking endpoint and it is the wrong one: it returns
   * the account - identity, roles, permissions - and carries no profile links
   * at all. Reading `links` off it produced `undefined` on every request, so
   * this picker showed its "add a link first" empty state to every host on the
   * site including ones with six links.
   *
   * `/users/{username}` is what the settings page already reads, so it is the
   * same query key and the same normalised shape - add a link over there and it
   * is selectable here without a refetch.
   */
  const { data: profile } = useQuery({
    queryKey: ["profile", room.username],
    queryFn: () =>
      api.get<{ links: { platform: string }[] }>(`/users/${room.username}`),
  });

  const update = useMutation({
    mutationFn: (platform: string | null) =>
      rooms.settings({ simulcast_platform: platform }),
    onSuccess: onChange,
  });

  const eligible = platforms.filter(
    (p) =>
      p.live && (profile?.links ?? []).some((l) => l.platform === p.platform),
  );

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <span className="meta-caps text-faint">Also streaming on</span>

      {eligible.length === 0 ? (
        <p className="font-mono text-[10px] leading-relaxed text-ghost">
          Add a Twitch, YouTube, TikTok, Instagram, Facebook, X or Discord link
          to your profile and you can point listeners at it from here.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            <Choice
              active={room.simulcast === null}
              onClick={() => update.mutate(null)}
              disabled={update.isPending}
              data-simulcast="none"
            >
              Audio only
            </Choice>

            {eligible.map((p) => (
              <Choice
                key={p.platform}
                active={room.simulcast?.platform === p.platform}
                onClick={() => update.mutate(p.platform)}
                disabled={update.isPending}
                data-simulcast={p.platform}
              >
                <PlatformIcon platform={p.platform} className="h-3.5 w-3.5" />
                {p.label}
              </Choice>
            ))}
          </div>

          {room.simulcast && (
            <p className="truncate font-mono text-[10px] text-ghost">
              listeners get a link to {room.simulcast.url}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Choice({
  active,
  children,
  ...rest
}: {
  active: boolean;
  children: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-pressed={active}
      {...rest}
      className={`flex items-center gap-1.5 rounded-[var(--radius-pill)] border px-3 py-1.5 font-mono text-[11px] transition-colors ${
        active
          ? "border-transparent bg-accent text-accent-ink"
          : "border-line text-soft hover:border-line-strong hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Whether a finished track rolls into the next one.
 *
 * On the deck beside the submissions policy, because both are things a host
 * reaches for *during* a broadcast rather than settings they configured once.
 *
 * Off is the reviewing mode: the room stops at the end of each track, the deck
 * keeps it loaded, and the review box stays pointed at it until the host
 * presses play. The consequence is dead air, so the control says so - a host
 * who turns this on absent-mindedly and walks away should have been told.
 */
function AutoPlaySetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (auto_play: boolean) => rooms.settings({ auto_play }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-faint">Auto play</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.auto_play}
          aria-label="Auto play"
          data-auto-play
          disabled={update.isPending}
          onClick={() => update.mutate(!room.auto_play)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.auto_play
              ? "border-transparent bg-accent"
              : "border-line bg-raised"
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.auto_play ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        {room.auto_play
          ? "The next track starts on its own when this one ends."
          : "The room holds at the end of each track. Nothing plays until you press play."}
      </p>
    </div>
  );
}

/**
 * Whether the room shows what is coming up.
 *
 * ## The count stays either way
 *
 * "How many are waiting" is what somebody deciding whether to submit needs, and
 * hiding it would make the submit button a coin toss. This hides the contents:
 * which tracks, in what order, and at what tier. A host running a set where the
 * running order is the show gets to keep it; a host whose boosts are the point
 * gets to publish it.
 *
 * ## Enforced on the server
 *
 * `ShowStationAction` withholds the rows rather than the client hiding them, so
 * turning this off is not one `fetch` away from being undone. This switch is
 * the control, not the mechanism.
 */
function ShowQueueSetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (show_queue: boolean) => rooms.settings({ show_queue }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-faint">Show the queue</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.show_queue}
          aria-label="Show the queue"
          data-show-queue
          disabled={update.isPending}
          onClick={() => update.mutate(!room.show_queue)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.show_queue
              ? "border-transparent bg-accent"
              : "border-line bg-raised"
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.show_queue ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        {room.show_queue
          ? "Listeners can see what is coming up, and which submissions were boosted."
          : "Listeners see how many are waiting, but not what they are. Your console is unchanged."}
      </p>
    </div>
  );
}

/**
 * The way in to the stream graphics.
 *
 * ## A button, not a URL
 *
 * There are three graphics and six appearance options behind this, which is
 * more than a settings panel in a sidebar can hold without becoming the sidebar.
 * It is also a thing a host does once while setting up a scene collection and
 * then never again, which is exactly what a dialog is for.
 *
 * ## And nothing of the token out here
 *
 * The console is a screen hosts screen-share while setting up, so the URL - the
 * only credential the overlay has - never renders until somebody opens the
 * dialog and asks for it.
 */
function OverlayLink({ token }: { token: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <span className="meta-caps text-faint">Stream graphics</span>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        now playing, next in queue and recently played — as browser sources for
        OBS, Streamlabs or TikTok Studio, on a transparent background
      </p>

      <button
        type="button"
        data-overlay-open-dialog
        onClick={() => setOpen(true)}
        className="mt-1 flex items-center justify-center gap-2 rounded-[var(--radius-control)] border border-line px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-line-strong hover:bg-raised"
      >
        <svg
          viewBox="0 0 16 16"
          className="h-4 w-4 text-soft"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <rect x="1.5" y="2.5" width="13" height="9" rx="1.5" />
          <path d="M5.5 14h5" />
        </svg>
        Set up overlays
      </button>

      {open && <OverlayDialog token={token} onClose={() => setOpen(false)} />}
    </div>
  );
}

/**
 * Whether this room takes tracks marked explicit.
 *
 * Beside the submissions policy because it is the same kind of decision - who
 * and what gets in - and a host setting one has usually just thought about the
 * other.
 *
 * On by default. A room that silently refused submissions because its owner
 * never opened a setting would be enforcing a rule nobody chose; turning it off
 * is the deliberate act, which is the right way round for a filter.
 */
function ExplicitSetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (accepts: boolean) =>
      rooms.settings({ accepts_explicit: accepts }),
    onSuccess: onChange,
  });

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <div className="flex items-center justify-between gap-3">
        <span className="meta-caps text-faint">Explicit tracks</span>

        <button
          type="button"
          role="switch"
          aria-checked={room.accepts_explicit}
          aria-label="Accept explicit tracks"
          data-accepts-explicit
          disabled={update.isPending}
          onClick={() => update.mutate(!room.accepts_explicit)}
          className={`h-6 w-11 shrink-0 rounded-[var(--radius-pill)] border transition-colors ${
            room.accepts_explicit
              ? "border-transparent bg-accent"
              : "border-line bg-raised"
          }`}
        >
          <span
            aria-hidden
            className={`block h-4 w-4 rounded-full bg-ink transition-transform ${
              room.accepts_explicit ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        {room.accepts_explicit
          ? "Anything goes. Tracks the artist marked explicit can be submitted."
          : "Refused at the door, and the submitter is told why. This applies to your own crate too."}
      </p>
    </div>
  );
}

/**
 * Who may put things in the queue.
 *
 * On the deck rather than buried in a settings page, because it is the control
 * a host reaches for *during* a broadcast - it is the answer to being brigaded,
 * and an answer that takes four clicks to find is not an answer.
 */
function SubmissionsSetting({
  room,
  onChange,
}: {
  room: Room;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (submissions: Room["submissions"]) =>
      rooms.settings({ submissions }),
    onSuccess: onChange,
  });

  const options: { id: Room["submissions"]; label: string }[] = [
    { id: "open", label: "Everyone" },
    { id: "followers", label: "Followers" },
    { id: "closed", label: "Closed" },
  ];

  return (
    <div className="flex flex-col gap-2 border-t border-line pt-5">
      <span className="meta-caps text-faint">Submissions</span>
      <div className="flex gap-1.5">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={room.submissions === option.id}
            data-submissions={option.id}
            onClick={() => update.mutate(option.id)}
            className={`flex-1 rounded-[var(--radius-control)] border px-2 py-2 font-mono text-[11px] transition-colors ${
              room.submissions === option.id
                ? "border-transparent bg-accent text-accent-ink"
                : "border-line text-soft hover:border-line-strong hover:text-ink"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      {/* The viewer this token was generated for now exists. */}
      {room.overlay_token && <OverlayLink token={room.overlay_token} />}
    </div>
  );
}
